import React from 'react';
import { useParams } from 'react-router';

import { Spinner } from '@admin/components/spinner';
import { facilitiesURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { FacilityUpdateInput, useFacilityDetailQuery, useFacilityUpdateMutation } from '@admin/schema';
import { BreadcrumbCurrent } from '@admin/styled/breadcrumb_current';
import { BreadcrumbNav } from '@admin/styled/breadcrumb_nav';
import { BreadcrumbSep } from '@admin/styled/breadcrumb_sep';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { CodeText } from '@admin/styled/code_text';
import { DetailGrid } from '@admin/styled/detail_grid';
import { DetailItem } from '@admin/styled/detail_item';
import { DetailLabel } from '@admin/styled/detail_label';
import { DetailValue } from '@admin/styled/detail_value';
import { ErrorBanner } from '@admin/styled/error_banner';
import { MutedPanel } from '@admin/styled/muted_panel';
import { MutedText } from '@admin/styled/muted_text';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageSections } from '@admin/styled/page_sections';
import { PageTitle } from '@admin/styled/page_title';
import { Pill } from '@admin/styled/pill';
import { PillGroup } from '@admin/styled/pill_group';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionTitle } from '@admin/styled/section_title';

import { WarehouseSitePanels } from '../warehouses/site_panels';
import { FacilityForm } from './facility_form';
import { FacilityHistorySection } from './facility_history_section';
import { FacilityRegionDefaults } from './facility_region_defaults';
import { toFacilityUpdateInput } from './facility_update_input';
import { formatFacilityAddress } from './format_address';
import { operatorLabel } from './operator_options';

export const FacilityDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const canUpdate = usePermissionKey('facility:update');
  const [editing, setEditing] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const [togglingActive, setTogglingActive] = React.useState(false);

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useFacilityDetailQuery({
    client,
    variables: { id: id! },
    skip: !id,
  });

  const [update, { loading: saving }] = useFacilityUpdateMutation({ client });

  const facility = data?.facility;
  const allServiceTypes = data?.facilityServiceTypes ?? [];
  useTitle(facility?.name ?? 'Facility');

  if (loading && !data) {
    return <Spinner />;
  }

  if (queryError) {
    return (
      <Page>
        <ErrorBanner role="alert">Could not load this facility. {queryError.message}</ErrorBanner>
      </Page>
    );
  }

  if (!facility || !id) {
    return (
      <Page>
        <MutedPanel>Facility not found.</MutedPanel>
      </Page>
    );
  }

  const formattedAddress = formatFacilityAddress(facility.address);

  const saveFacility = async (input: FacilityUpdateInput, options?: { closeEditor?: boolean }) => {
    setError(undefined);
    const result = await update({ variables: { id, input } });
    const payload = result.data?.facilityUpdate;

    if (payload?.error) {
      setError(payload.error);
      return false;
    }

    if (payload?.status === 'OK') {
      if (options?.closeEditor) {
        setEditing(false);
      }
      await refetch();
      return true;
    }

    return false;
  };

  const onSubmit = async (input: FacilityUpdateInput) => {
    await saveFacility(input, { closeEditor: true });
  };

  const onToggleActive = async () => {
    if (togglingActive) {
      return;
    }
    setTogglingActive(true);
    const input = { ...toFacilityUpdateInput(facility), active: !facility.active };
    await saveFacility(input);
    setTogglingActive(false);
  };

  return (
    <Page>
      <BreadcrumbNav aria-label="Breadcrumb">
        <ButtonLink to={facilitiesURL()}>Facilities</ButtonLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>{facility.name}</BreadcrumbCurrent>
      </BreadcrumbNav>

      <PageHeader>
        <PageTitle>{facility.name}</PageTitle>
      </PageHeader>

      {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

      <Section>
        {editing ? (
          <FacilityForm
            initialValues={toFacilityUpdateInput(facility)}
            allServiceTypes={allServiceTypes}
            onSubmit={onSubmit}
            onCancel={() => {
              setEditing(false);
              setError(undefined);
            }}
            saving={saving}
          />
        ) : (
          <Card>
            <CardToolbar>
              <SectionTitle>Overview</SectionTitle>
              <SectionActions>
                <Pill tone={facility.active ? 'success' : 'muted'}>{facility.active ? 'Active' : 'Inactive'}</Pill>
                {canUpdate &&
                  (facility.active ? (
                    <Button variant="danger" size="sm" type="button" disabled={togglingActive} onClick={onToggleActive}>
                      {togglingActive ? 'Saving…' : 'Deactivate'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      disabled={togglingActive}
                      onClick={onToggleActive}
                    >
                      {togglingActive ? 'Saving…' : 'Activate'}
                    </Button>
                  ))}
                {canUpdate && (
                  <Button variant="secondary" size="sm" type="button" onClick={() => setEditing(true)}>
                    Edit details
                  </Button>
                )}
              </SectionActions>
            </CardToolbar>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Code</DetailLabel>
                <DetailValue>
                  <CodeText>{facility.code}</CodeText>
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Time zone</DetailLabel>
                <DetailValue>{facility.tz}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Address</DetailLabel>
                <DetailValue>{formattedAddress ?? <MutedText>Not set</MutedText>}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Regions</DetailLabel>
                <DetailValue>
                  {facility.regions.length > 0 ? (
                    <PillGroup>
                      {facility.regions.map((region) => (
                        <Pill key={region.id} tone="muted">
                          {region.name}
                        </Pill>
                      ))}
                    </PillGroup>
                  ) : (
                    <MutedText>None</MutedText>
                  )}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Service Offerings</DetailLabel>
                <DetailValue>
                  {facility.serviceOfferings.length > 0 ? (
                    <PillGroup>
                      {facility.serviceOfferings.map((offering) => (
                        <Pill key={offering.id} tone="accent">
                          {offering.serviceType.name} · {operatorLabel(offering.operator)}
                        </Pill>
                      ))}
                    </PillGroup>
                  ) : (
                    <MutedText>None</MutedText>
                  )}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
        )}
      </Section>

      {!editing && (
        <PageSections>
          <FacilityRegionDefaults
            regions={facility.regions}
            warehouseId={facility.warehouse?.id}
            depotId={facility.depot?.id}
            warehouseActive={facility.warehouse?.active ?? false}
            depotActive={facility.depot?.active ?? false}
          />

          {facility.warehouse?.id && <WarehouseSitePanels warehouseId={facility.warehouse.id} />}

          <FacilityHistorySection
            facilityId={facility.id}
            warehouseId={facility.warehouse?.id}
            depotId={facility.depot?.id}
            timezone={facility.tz}
          />
        </PageSections>
      )}
    </Page>
  );
};
